import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Heart, ArrowRight, Loader2, AlertCircle, Mail } from 'lucide-react';
import api from '@/api/client';
import Seo from '@/seo/Seo';

const MAX_POLLS = 8;       // 8 × 2s = ~16s max wait
const POLL_INTERVAL = 2000;

export default function DonateSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState({ phase: 'polling', tx: null, error: null });
  const attemptsRef = useRef(0);
  const stoppedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: 'error', tx: null, error: 'No session_id in URL.' });
      return undefined;
    }

    const poll = async () => {
      if (stoppedRef.current) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/donations/status/${sessionId}`);
        if (data.paid) {
          stoppedRef.current = true;
          setState({ phase: 'paid', tx: data, error: null });
          return;
        }
        if (data.status === 'expired') {
          stoppedRef.current = true;
          setState({ phase: 'expired', tx: data, error: null });
          return;
        }
        if (attemptsRef.current >= MAX_POLLS) {
          stoppedRef.current = true;
          setState({ phase: 'timeout', tx: data, error: null });
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (err) {
        if (attemptsRef.current >= MAX_POLLS) {
          stoppedRef.current = true;
          setState({ phase: 'error', tx: null, error: err?.response?.data?.detail || 'Could not verify your donation.' });
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      }
    };

    poll();
    return () => { stoppedRef.current = true; };
  }, [sessionId]);

  const isPaid = state.phase === 'paid';

  return (
    <div>
      <Seo
        title="Donation Received — Thank You | Ministry of Jesus"
        titleExact
        description="Thanksgiving for every generous gift to Ministry of Jesus, Dublin. Your donation has been received."
        path="/donate/success"
        noindex
      />
      <section className="py-20 bg-moj-cream min-h-[70vh] flex items-center">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center card-moj p-8 sm:p-12" data-testid="donate-success-card">
          {state.phase === 'polling' && (
            <>
              <Loader2 size={48} className="text-moj-gold-deep mx-auto mb-5 animate-spin"/>
              <h1 className="font-serif text-3xl text-moj-purple-deep mb-2">Confirming your donation…</h1>
              <p className="text-moj-charcoal/80">This usually takes only a few seconds.</p>
            </>
          )}

          {isPaid && (
            <>
              <div className="w-20 h-20 rounded-full gradient-purple flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 size={40} className="text-moj-gold"/>
              </div>
              <h1 className="font-serif text-3xl sm:text-4xl text-moj-purple-deep mb-3" data-testid="donate-success-heading">
                {state.tx.is_recurring ? 'Recurring gift activated' : 'Donation received with thanksgiving'}
              </h1>
              <p className="text-moj-charcoal/85 max-w-md mx-auto mb-6">
                May the Lord bless every seed sown for His Kingdom.{' '}
                {state.tx.is_recurring ? (
                  <>Your first gift of <strong className="text-moj-purple-deep">€{state.tx.amount_total.toFixed(2)}</strong> to <strong>{state.tx.category_label}</strong> has been received, and a new gift will be charged every <strong>{state.tx.interval === 'week' ? 'week' : 'month'}</strong> until you choose to stop.</>
                ) : (
                  <>We’ve received your gift of <strong className="text-moj-purple-deep">€{state.tx.amount_total.toFixed(2)}</strong> to <strong>{state.tx.category_label}</strong>.</>
                )}
              </p>
              <div className="inline-flex items-center gap-2 bg-moj-cream-soft px-4 py-2 rounded-full text-sm text-moj-charcoal/80 mb-6">
                <Mail size={14} className="text-moj-gold-deep"/> A receipt has been emailed to <strong className="ml-1">{state.tx.donor_email}</strong>
              </div>
              {state.tx.is_recurring && (
                <div className="text-left bg-moj-gold/10 border border-moj-gold/30 p-4 rounded-md mb-6 text-sm">
                  <div className="font-bold text-moj-purple-deep mb-1">Managing your recurring gift</div>
                  <p className="text-moj-charcoal/85">
                    Stripe’s receipt email contains a secure link to view, update or cancel your recurring giving at any time. You can also <Link to="/contact" className="text-moj-purple underline">contact us</Link> and we’ll handle it for you.
                  </p>
                </div>
              )}
              {state.tx.prayer_note && (
                <div className="text-left bg-moj-purple/5 border-l-4 border-moj-gold p-4 rounded-md mb-6">
                  <div className="text-xs uppercase tracking-wider text-moj-gold-deep font-bold mb-1">Your Prayer Note</div>
                  <p className="italic text-moj-purple-deep">“{state.tx.prayer_note}”</p>
                  <p className="text-xs text-moj-charcoal/70 mt-2">Our intercessors will pray over this request.</p>
                </div>
              )}
              <p className="italic text-moj-purple-deep border-l-2 border-moj-gold pl-4 max-w-md mx-auto text-left">
                “Each of you should give what you have decided in your heart to give… for God loves a cheerful giver.” — 2 Corinthians 9:7
              </p>
            </>
          )}

          {state.phase === 'expired' && (
            <>
              <AlertCircle size={48} className="text-moj-gold-deep mx-auto mb-4"/>
              <h1 className="font-serif text-3xl text-moj-purple-deep mb-2">Session expired</h1>
              <p className="text-moj-charcoal/85 mb-6">Your checkout session expired before payment completed. Please start a new donation.</p>
            </>
          )}

          {state.phase === 'timeout' && (
            <>
              <AlertCircle size={48} className="text-moj-gold-deep mx-auto mb-4"/>
              <h1 className="font-serif text-3xl text-moj-purple-deep mb-2">Still processing</h1>
              <p className="text-moj-charcoal/85 mb-6">Your payment is taking a little longer than usual. You’ll receive an email receipt from Stripe when it completes. If you have any concerns, please contact us.</p>
            </>
          )}

          {state.phase === 'error' && (
            <>
              <AlertCircle size={48} className="text-red-600 mx-auto mb-4"/>
              <h1 className="font-serif text-3xl text-moj-purple-deep mb-2">Something went wrong</h1>
              <p className="text-moj-charcoal/85 mb-6">{state.error}</p>
            </>
          )}

          <div className="flex flex-wrap justify-center gap-3 mt-6">
            <Link to="/" className="btn-primary" data-testid="donate-success-home">Back to Home <ArrowRight size={16}/></Link>
            <Link to="/donate" className="btn-outline"><Heart size={16}/> Give Again</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
